import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getServiceRequestPinnedResource,
  pinServiceRequestResource,
  unpinServiceRequestResource,
} from 'src/services/client';
import { PinnedNoteResource } from '../types';

interface PinnedResourceEnvelope {
  statusCode: number;
  message: string;
  data: any;
  warnings: string[] | null;
}

// legacy/workaround: the pinned-resource GET returns the raw Resource row with the
// note content serialized in a `data` JSON string, so title/body/author aren't
// populated at the top level. Deserialize once here so the preview card renders a
// flat, typed note; fall back to any fields the API already surfaced top-level.
const normalizePinnedNote = (raw: any): PinnedNoteResource => {
  let content: any = {};

  if (typeof raw.data === 'string' && raw.data.trim().length > 0) {
    try {
      content = JSON.parse(raw.data);
    } catch (err) {
      console.warn('Failed to parse pinned note data blob', err);
    }
  }

  return {
    id: raw.id ?? content.id ?? '',
    documentType: raw.documentType ?? content.documentType ?? 'NOTES',
    title: content.title ?? raw.title ?? '',
    body: content.body ?? raw.body ?? '',
    createdAt: content.createdAt ?? raw.createdAt ?? '',
    author: content.author ?? raw.author ?? null,
    visibilityType: content.visibilityType ?? raw.visibilityType ?? '',
    data: typeof raw.data === 'string' ? raw.data : '',
  };
};

export interface UsePinnedResourceReturn {
  pinnedResource: PinnedNoteResource | null;
  loading: boolean;
  error: string | null;
  fetchPinnedResource: (serviceRequestId: string) => Promise<void>;
  pinResource: (serviceRequestId: string, resourceId: string) => Promise<void>;
  unpinResource: (serviceRequestId: string) => Promise<void>;
}

export const usePinnedResource = (token?: string): UsePinnedResourceReturn => {
  const [pinnedResource, setPinnedResource] =
    useState<PinnedNoteResource | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPinnedResource = async (
    serviceRequestId: string
  ): Promise<void> => {
    if (!serviceRequestId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response: any = await getServiceRequestPinnedResource(
        serviceRequestId,
        token
      );

      if (response.ok || response.status === 200) {
        const envelope: PinnedResourceEnvelope = await response.json();

        const normalizedNote: PinnedNoteResource | null = envelope.data
          ? normalizePinnedNote(envelope.data)
          : null;

        setPinnedResource(normalizedNote);
        return;
      }

      setPinnedResource(null);
    } catch (err: any) {
      console.error('Error fetching pinned resource:', err);
      setError(err.message ?? 'Failed to load the pinned note.');
    } finally {
      setLoading(false);
    }
  };

  const pinResource = async (
    serviceRequestId: string,
    resourceId: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await pinServiceRequestResource(
        serviceRequestId,
        { resourceId },
        token
      );

      if (response.ok || response.status === 200) {
        toast.success('ⓘ Note pinned.', {
          autoClose: 5000,
          position: 'top-center',
        });

        await fetchPinnedResource(serviceRequestId);
        return;
      }

      throw new Error('Failed to pin the note.');
    } catch (err: any) {
      console.error('Error pinning resource:', err);

      const message: string =
        err.message ?? 'Failed to pin the note. Please try again.';
      setError(message);

      toast.error(`🚫 ${message}`, {
        autoClose: 8000,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const unpinResource = async (serviceRequestId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await unpinServiceRequestResource(
        serviceRequestId,
        token
      );

      if (response.ok || response.status === 200) {
        setPinnedResource(null);

        toast.success('ⓘ Note unpinned.', {
          autoClose: 5000,
          position: 'top-center',
        });

        return;
      }

      throw new Error('Failed to unpin the note.');
    } catch (err: any) {
      console.error('Error unpinning resource:', err);

      const message: string =
        err.message ?? 'Failed to unpin the note. Please try again.';
      setError(message);

      toast.error(`🚫 ${message}`, {
        autoClose: 8000,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    pinnedResource,
    loading,
    error,
    fetchPinnedResource,
    pinResource,
    unpinResource,
  };
};